const DRAG_SELECTION_LOCK_CLASS = 'otto-drag-selection-locked';
const DRAG_THRESHOLD_PX = 4;

const SELECTABLE_TARGET_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable]',
  '.select-text',
  '[data-allow-text-selection]',
].join(',');

let lockDepth = 0;
let installed = false;
let pendingDragStart: { x: number; y: number } | null = null;
let releaseGlobalDragLock: (() => void) | null = null;

function isSelectableTarget(target: EventTarget | null): boolean {
  return (
    typeof Element !== 'undefined' && target instanceof Element && Boolean(target.closest(SELECTABLE_TARGET_SELECTOR))
  );
}

function clearCurrentSelection() {
  if (typeof window === 'undefined') return;
  const selection = window.getSelection();
  if (selection?.rangeCount) {
    selection.removeAllRanges();
  }
}

export function lockDragTextSelection(): () => void {
  if (typeof document === 'undefined') return () => {};

  let released = false;
  lockDepth += 1;
  document.documentElement.classList.add(DRAG_SELECTION_LOCK_CLASS);
  clearCurrentSelection();

  return () => {
    if (released) return;
    released = true;
    lockDepth = Math.max(0, lockDepth - 1);
    if (lockDepth === 0) {
      document.documentElement.classList.remove(DRAG_SELECTION_LOCK_CLASS);
    }
  };
}

function finishGlobalDragSelectionLock() {
  pendingDragStart = null;
  releaseGlobalDragLock?.();
  releaseGlobalDragLock = null;
}

function onGlobalMouseDown(event: MouseEvent) {
  if (event.button !== 0 || isSelectableTarget(event.target)) {
    return;
  }

  pendingDragStart = { x: event.clientX, y: event.clientY };
}

function onGlobalMouseMove(event: MouseEvent) {
  if (!pendingDragStart || (event.buttons & 1) !== 1) {
    return;
  }

  if (releaseGlobalDragLock) {
    event.preventDefault();
    return;
  }

  const movedEnough =
    Math.abs(event.clientX - pendingDragStart.x) >= DRAG_THRESHOLD_PX ||
    Math.abs(event.clientY - pendingDragStart.y) >= DRAG_THRESHOLD_PX;

  if (!movedEnough) return;

  releaseGlobalDragLock = lockDragTextSelection();
  event.preventDefault();
}

export function installDragSelectionGuard() {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener('mousedown', onGlobalMouseDown, true);
  document.addEventListener('mousemove', onGlobalMouseMove, true);
  document.addEventListener('mouseup', finishGlobalDragSelectionLock, true);
  document.addEventListener('mouseleave', finishGlobalDragSelectionLock, true);
  if (typeof window !== 'undefined') {
    window.addEventListener('blur', finishGlobalDragSelectionLock);
  }
}
